import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { CurateWordmark } from "@/components/Brand";
import { toast } from "sonner";

export default function Register() {
  const { register, user } = useAuth();
  const [brand, setBrand] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    const r = await register(email, password, brand);
    setBusy(false);
    if (r.ok) {
      toast.success("Brand created. Let's set things up.");
      nav("/dashboard", { replace: true });
    } else {
      toast.error(r.error || "Registration failed");
    }
  };

  return (
    <div className="min-h-screen bg-white grid lg:grid-cols-2">
      <div className="relative hidden lg:block bg-[#0F1210] text-white p-12">
        <CurateWordmark size={28} link={false} onDark />
        <div className="mt-32">
          <div className="label-overline text-white/50">Step 01</div>
          <h2 className="mt-4 font-display font-black tracking-tighter text-5xl leading-[0.95]">
            Create your<br />brand account.
          </h2>
          <p className="mt-6 max-w-md text-white/70 text-sm leading-relaxed">
            Free during early access. Bring your products, design your questions, deploy to any device — tablet, phone, or in-store screen.
          </p>
        </div>
        <div className="absolute bottom-12 left-12 right-12 grid grid-cols-3 gap-px bg-white/10">
          {["Catalog", "Questions", "Kiosk"].map((s, i) => (
            <div key={s} className="bg-[#0a0a0a] p-4">
              <div className="label-overline text-white/40">0{i + 1}</div>
              <div className="font-display font-bold mt-2">{s}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center px-8 py-16">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="register-form">
          <Link to="/" className="label-overline">← Back</Link>
          <h1 className="mt-6 font-display font-black tracking-tighter text-4xl">Create account</h1>
          <p className="mt-2 text-sm text-[#52525b]">Start with your brand name.</p>

          <div className="mt-8 space-y-5">
            <div>
              <Label className="label-overline">Brand name</Label>
              <Input
                required
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className="mt-2 rounded-none border-[#e5e5e5] focus-visible:ring-[#0F1210] focus-visible:ring-1"
                data-testid="register-brand"
                placeholder="Acme Studio"
              />
            </div>
            <div>
              <Label className="label-overline">Email</Label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-none border-[#e5e5e5] focus-visible:ring-[#0F1210] focus-visible:ring-1"
                data-testid="register-email"
                placeholder="founder@acme.co"
              />
            </div>
            <div>
              <Label className="label-overline">Password</Label>
              <Input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-none border-[#e5e5e5] focus-visible:ring-[#0F1210] focus-visible:ring-1"
                data-testid="register-password"
                placeholder="At least 6 characters"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={busy}
            data-testid="register-submit-button"
            className="mt-8 w-full h-12 rounded-none bg-[#0F1210] hover:bg-black text-white text-base"
          >
            {busy ? "Creating…" : "Create account"}
          </Button>

          <div className="mt-6 text-sm text-[#52525b]">
            Already have an account?{" "}
            <Link to="/login" className="text-[#0F1210] underline" data-testid="link-to-login">
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
